import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Loader2, CheckCircle, Clock, Calendar as CalendarIcon } from 'lucide-react';
import { timeAttendanceApi, employeeApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { formatDate as formatDateUtil, formatTime as formatTimeUtil } from '../../lib/utils';
import { cn } from '../../lib/utils';

interface TimeEntryFormData {
  employee_id: string;
  date: string;
  clock_in: string;
  clock_out: string;
  break_duration: number;
  overtime_hours: number;
}

interface TimeEntryRecord {
  id: string;
  employee_id: string;
  clock_in?: string;
  clock_out?: string;
  break_duration?: number;
  total_hours?: number;
  overtime_hours?: number;
  entry_status?: string;
  is_approved?: boolean;
}

// Custom time input component for better cross-browser support
const TimeInput: React.FC<{
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ id, value, onChange, placeholder = "HH:MM" }) => {
  const [hours, setHours] = useState(value ? value.split(':')[0] : '');
  const [minutes, setMinutes] = useState(value ? value.split(':')[1] : '');

  const formatAndUpdateTime = (newHours: string, newMinutes: string) => {
    let h = parseInt(newHours) || 0;
    if (h > 23) h = 23;
    if (h < 0) h = 0;

    let m = parseInt(newMinutes) || 0;
    if (m > 59) m = 59;
    if (m < 0) m = 0;

    const formattedHours = h.toString().padStart(2, '0');
    const formattedMinutes = m.toString().padStart(2, '0');

    setHours(formattedHours);
    setMinutes(formattedMinutes);

    if (newHours !== '' || newMinutes !== '') {
      onChange(`${formattedHours}:${formattedMinutes}`);
    } else {
      onChange('');
    }
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    setHours(val);
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMinutes(val);
  };

  const handleBlur = () => {
    if (hours !== '' || minutes !== '') {
      formatAndUpdateTime(hours || '0', minutes || '0');
    }
  };

  React.useEffect(() => {
    if (value) {
      const parts = value.split(':');
      setHours(parts[0] || '');
      setMinutes(parts[1] || '');
    } else {
      setHours('');
      setMinutes('');
    }
  }, [value]);

  return (
    <div className="flex items-center border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-900 focus-within:ring-2 focus-within:ring-blue-500/20 shadow-sm">
      <div className="flex items-center px-3 text-slate-400 shrink-0">
        <Clock className="h-4 w-4" />
      </div>
      <input
        type="text"
        inputMode="numeric"
        placeholder="HH"
        value={hours}
        onChange={handleHoursChange}
        onBlur={handleBlur}
        className="w-12 py-2 text-center border-0 focus:outline-none bg-transparent text-slate-800 dark:text-slate-200 font-bold text-sm"
        maxLength={2}
      />
      <span className="text-slate-400 font-bold">:</span>
      <input
        type="text"
        inputMode="numeric"
        placeholder="MM"
        value={minutes}
        onChange={handleMinutesChange}
        onBlur={handleBlur}
        className="w-12 py-2 text-center border-0 focus:outline-none bg-transparent text-slate-800 dark:text-slate-200 font-bold text-sm"
        maxLength={2}
      />
    </div>
  );
};

const TimeAttendance: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState<string>('all');
  const [formData, setFormData] = useState<TimeEntryFormData>({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    clock_in: '',
    clock_out: '',
    break_duration: 0,
    overtime_hours: 0,
  });

  const { data: timeEntries = [], isLoading, error } = useQuery<TimeEntryRecord[]>({
    queryKey: ['timeEntries', filterEmployee],
    queryFn: () => timeAttendanceApi.timeEntries.getAll({
      employee_id: filterEmployee !== 'all' ? filterEmployee : undefined,
    }),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  const createMutation = useMutation({
    mutationFn: timeAttendanceApi.timeEntries.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      setIsDialogOpen(false);
      resetForm();
      toast({ title: 'Time entry created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, approved_by }: { id: string; approved_by: string }) =>
      timeAttendanceApi.timeEntries.approve(id, approved_by),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      toast({ title: 'Time entry approved' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = () => {
    if (!formData.employee_id || !formData.date || !formData.clock_in) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }

    const clockInDate = buildDateTime(formData.date, formData.clock_in);
    if (!clockInDate) {
      toast({ title: 'Error', description: 'Invalid clock in time', variant: 'destructive' });
      return;
    }

    const payload: Record<string, any> = {
      employee_id: formData.employee_id,
      clock_in: clockInDate.toISOString(),
      break_duration: formData.break_duration || 0,
      overtime_hours: formData.overtime_hours || 0,
    };

    if (formData.clock_out) {
      const clockOutDate = buildDateTime(formData.date, formData.clock_out);
      if (!clockOutDate) {
        toast({ title: 'Error', description: 'Invalid clock out time', variant: 'destructive' });
        return;
      }
      if (clockOutDate <= clockInDate) {
        toast({ title: 'Error', description: 'Clock out must be after clock in', variant: 'destructive' });
        return;
      }
      payload.clock_out = clockOutDate.toISOString();
    }

    createMutation.mutate(payload);
  };

  const handleApprove = (entryId: string) => {
    approveMutation.mutate({ id: entryId, approved_by: 'current-user-id' });
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      date: new Date().toISOString().split('T')[0],
      clock_in: '',
      clock_out: '',
      break_duration: 0,
      overtime_hours: 0,
    });
  };

  const getEmployeeName = (employeeId: string) => {
    const emp = employees.find((e: any) => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  const getEntryStatus = (entry: TimeEntryRecord) => {
    if (entry.entry_status) {
      return entry.entry_status;
    }
    return entry.is_approved ? 'APPROVED' : 'PENDING';
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      APPROVED: 'bg-emerald-50 text-emerald-705 border-emerald-250/50 dark:bg-emerald-950/20 dark:text-emerald-400',
      PENDING: 'bg-amber-50 text-amber-705 border-amber-250/50 dark:bg-amber-950/20 dark:text-amber-400',
    };
    const classNames = colors[status.toUpperCase()] || 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0", classNames)} variant="outline">
        {status}
      </Badge>
    );
  };

  const buildDateTime = (dateStr: string, timeStr: string) => {
    if (!dateStr || !timeStr) return null;
    return new Date(`${dateStr}T${timeStr}`);
  };

  const formatDate = (value?: string) => {
    if (!value) return '—';
    return formatDateUtil(value);
  };

  const formatTime = (value?: string) => {
    if (!value) return '--:--';
    return formatTimeUtil(value);
  };

  const calculateHours = (clockIn?: string, clockOut?: string, breakDuration: number = 0) => {
    if (!clockIn || !clockOut) return 0;
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    return Math.max(0, diff - (breakDuration / 60));
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-650" />
        <span className="ml-2 mt-3 font-bold text-xs text-slate-400">Loading time entries...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-2xl text-xs font-semibold m-6">
        <p className="font-bold text-sm">Error loading time entries</p>
        <p className="mt-1">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Track employee time and attendance</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          <Select value={filterEmployee} onValueChange={setFilterEmployee}>
            <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-9 w-[180px] rounded-xl shadow-sm">
              <SelectValue placeholder="Filter by employee" />
            </SelectTrigger>
            <SelectContent className="border-slate-105 dark:border-slate-800">
              <SelectItem value="all" className="text-xs font-bold">All Employees</SelectItem>
              {employees.map((emp: any) => (
                <SelectItem key={emp.id} value={emp.id} className="text-xs font-bold">
                  {emp.first_name} {emp.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={() => setIsDialogOpen(true)} 
          > 
            <Plus className="h-4 w-4 mr-1.5 shrink-0" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Main Table Card */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Time Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-2 border-slate-55 dark:border-slate-800">
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Employee</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Date</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Clock In</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Clock Out</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Hours</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Overtime</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-right px-8">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {timeEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-20 px-8 text-slate-400 font-semibold text-xs animate-pulse">
                    No time entries found. Add your first entry.
                  </TableCell>
                </TableRow>
              ) : (
                timeEntries.map((entry) => (
                  <TableRow key={entry.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-850/50">
                    <TableCell className="px-8 py-4">
                      <div className="flex items-center space-x-2.5">
                        <div className="w-7 h-7 rounded-lg bg-[#143A62]/10 dark:bg-blue-650/20 text-[#143A62] dark:text-blue-400 font-bold text-[11px] flex items-center justify-center tracking-wider shrink-0 uppercase">
                          {getEmployeeName(entry.employee_id).split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <span className="font-bold text-xs text-[#143A62] dark:text-slate-200">{getEmployeeName(entry.employee_id)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="h-4 w-4 text-blue-500 shrink-0" />
                        <span className="font-bold text-xs text-slate-700 dark:text-slate-300">{formatDate(entry.clock_in)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs font-semibold text-slate-655 py-4">{formatTime(entry.clock_in)}</TableCell>
                    <TableCell className="text-xs font-semibold text-slate-655 py-4">{formatTime(entry.clock_out)}</TableCell>
                    <TableCell className="text-xs font-bold text-slate-700 dark:text-slate-300 py-4">
                      {calculateHours(entry.clock_in, entry.clock_out, entry.break_duration ?? 0).toFixed(2)}h
                    </TableCell>
                    <TableCell className="text-xs font-bold text-slate-705 py-4">
                      {Number(entry.overtime_hours ?? 0).toFixed(2)}h
                    </TableCell>
                    <TableCell className="py-4">{getStatusBadge(getEntryStatus(entry))}</TableCell>
                    <TableCell className="px-8 py-4 text-right">
                      {getEntryStatus(entry) === 'PENDING' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleApprove(entry.id)}
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            resetForm();
          }
        }}
      >
        <DialogContent className="w-[min(96vw,550px)] max-w-[550px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">Add Time Entry</DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Record employee time attendance
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="employee_id" className="text-xs font-bold text-slate-400 tracking-wider">Employee *</Label>
              <Select
                value={formData.employee_id}
                onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
              >
                <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-10 rounded-xl shadow-sm">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="border-slate-105 dark:border-slate-800">
                  {employees.map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id} className="text-xs font-bold">
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="entry_date" className="text-xs font-bold text-slate-400 tracking-wider">Date *</Label>
              <Input
                id="entry_date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="clock_in" className="text-xs font-bold text-slate-400 tracking-wider">Clock In *</Label>
                <TimeInput
                  id="clock_in"
                  value={formData.clock_in}
                  onChange={(value) => setFormData({ ...formData, clock_in: value })}
                  placeholder="09:00"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="clock_out" className="text-xs font-bold text-slate-400 tracking-wider">Clock Out</Label>
                <TimeInput
                  id="clock_out"
                  value={formData.clock_out}
                  onChange={(value) => setFormData({ ...formData, clock_out: value })}
                  placeholder="17:00"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="break_duration" className="text-xs font-bold text-slate-400 tracking-wider">Break (minutes)</Label>
                <Input
                  id="break_duration"
                  type="number"
                  min={0}
                  value={formData.break_duration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      break_duration: Number(e.target.value) >= 0 ? Number(e.target.value) : 0,
                    })
                  }
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="overtime_hours" className="text-xs font-bold text-slate-400 tracking-wider">Overtime (hours)</Label>
                <Input
                  id="overtime_hours"
                  type="number"
                  step="0.5"
                  min={0}
                  value={formData.overtime_hours}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      overtime_hours: Number(e.target.value) >= 0 ? Number(e.target.value) : 0,
                    })
                  }
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeAttendance;